import type { ReactNode } from "react";

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  // Rendered in UTC, matching every timestamp the platform stores. Showing
  // local time would make a dashboard reading disagree with the audit log
  // and the EA's own reports for no benefit.
  return parsed.toISOString().replace("T", " ").replace(".000Z", "Z").slice(0, 19) + "Z";
}

/**
 * A timestamp plus how long ago it was, flagged when it falls behind.
 * A heartbeat that stopped an hour ago looks identical to a fresh one if
 * only the raw timestamp is shown, which is exactly the failure an operator
 * needs to notice at a glance.
 */
export function Stale({
  at,
  serverTime,
  warnAfterSeconds = 120,
}: {
  at: string | null;
  serverTime: string;
  warnAfterSeconds?: number;
}) {
  if (!at) return <span className="badge warn">never</span>;

  const ageSeconds = (new Date(serverTime).getTime() - new Date(at).getTime()) / 1000;
  const label = ageSeconds < 90 ? `${Math.max(0, Math.round(ageSeconds))}s ago` : describeAge(ageSeconds);

  return (
    <span className={ageSeconds > warnAfterSeconds ? "badge warn" : "badge ok"} title={formatTime(at)}>
      {label}
    </span>
  );
}

function describeAge(seconds: number): string {
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}

/** Loading / error / empty, so no page reimplements the three of them. */
export function PageState({
  loading,
  error,
  empty,
  emptyMessage = "Nothing to show yet.",
  children,
}: {
  loading: boolean;
  error: string | null;
  empty?: boolean;
  emptyMessage?: string;
  children: ReactNode;
}) {
  if (error) return <div className="notice error">{error}</div>;
  if (loading) return <p className="dim">Loading…</p>;
  if (empty) return <p className="dim">{emptyMessage}</p>;
  return <>{children}</>;
}

const STATE_TONE: Record<string, string> = {
  EXECUTED: "ok",
  PARTIAL: "warn",
  PENDING: "",
  DELIVERED: "",
  ACKNOWLEDGED: "warn",
  REJECTED_LOCAL: "danger",
  REJECTED_BROKER: "danger",
  EXPIRED: "danger",
  // UNKNOWN is the one state that always demands a human: the broker outcome
  // is unresolved, so it is styled as loudly as an outright failure.
  UNKNOWN: "danger",
};

export function CommandState({ state }: { state: string }) {
  return <span className={`badge ${STATE_TONE[state] ?? ""}`}>{state}</span>;
}
